/**
 * Consultas de medios como estado de React.
 *
 * Con `useSyncExternalStore` y no con `useState` + `useEffect`: el valor vive
 * en el navegador, no en el componente, y esta es la API que React 19 ofrece
 * para leer una fuente externa sin desincronizarse en el primer pintado.
 *
 * Los puntos de corte en píxeles están arriba, junto a los de `sigfisc.css`,
 * por la misma razón que allí: una variable CSS no se puede usar dentro de
 * `@media`, así que el valor se escribe dos veces y conviene que las dos
 * copias se lean juntas.
 */
import { useCallback, useSyncExternalStore } from 'react'

/**
 * Si el puntero puede posarse. **No es lo mismo que «pantalla ancha».**
 *
 * Un portátil con pantalla táctil tiene las dos cosas, y una tableta en
 * horizontal es ancha pero no tiene hover. Lo que decide si la revelación al
 * posarse sirve de algo es el puntero, no el ancho, así que se pregunta por el
 * puntero.
 */
export const HAY_HOVER = '(hover: hover) and (pointer: fine)'

/**
 * Si el sistema pide menos movimiento.
 *
 * No es una preferencia estética: a algunas personas el desplazamiento animado
 * les produce mareo, y el sistema operativo ya recoge ese ajuste. Quien lo
 * tenga puesto recibe el salto seco.
 */
export const MENOS_MOVIMIENTO = '(prefers-reduced-motion: reduce)'

export function useMedia(consulta: string): boolean {
  const suscribir = useCallback(
    (avisar: () => void) => {
      const lista = window.matchMedia(consulta)
      lista.addEventListener('change', avisar)
      return () => {
        lista.removeEventListener('change', avisar)
      }
    },
    [consulta],
  )

  return useSyncExternalStore(
    suscribir,
    () => window.matchMedia(consulta).matches,
    // En servidor no hay `matchMedia`. Este proyecto no renderiza en servidor,
    // pero el valor tiene que existir igual: `useSyncExternalStore` lo exige y
    // devolver `false` es lo prudente —se asume lo táctil, que es el caso con
    // menos gestos disponibles—.
    () => false,
  )
}
